import {Helmet} from '@dr.pogodin/react-helmet';
import {Card, DataStatus, Segment} from 'platform/components';
import {
  Box,
  getSize,
  getValueByDevice,
  Heading,
  Hide,
  HStack,
  Text,
  useDevice,
  VStack,
} from 'platform/foundation';

import {useState} from 'react';

import {isNil, isNotNil} from 'ramda';

import {suffixTestId, TestIdProps} from 'shared/web';

import {Alert} from '../../components/Alert/Alert';
import {PageBackgroundGradient} from '../../components/PageBackgroundGradient/PageBackgroundGradient';
import {StickyContainer} from '../../components/StickyContainer/StickyContainer';
import {VehicleHeader} from '../../components/VehicleHeader/VehicleHeader';
import {useCatalogue} from '../../hooks/useCatalogue';
import {usePriceReport} from '../../hooks/usePriceReport';
import i18n from '../../i18n/i18n';
import {VehicleParametersCard} from '../Overview/(sections)/VehicleParametersCard/VehicleParametersCard';
import {useIsPrintForced} from '../Overview/hooks/useIsPrintForced';

const PRINT_CONTENT_WIDTH = 240;

interface ReasoningPageProps extends TestIdProps {}

export function ReasoningPage(props: ReasoningPageProps) {
  const {priceReport, requestData} = usePriceReport();
  const catalogue = useCatalogue();
  const device = useDevice();
  const {isPrint, getResponsiveValue} = useIsPrintForced();
  const [selectedSegment, setSelectedSegment] = useState<'buying' | 'selling'>('buying');

  const maxContentWidth = getResponsiveValue(
    '100%',
    `calc(100% - ${getSize(86)})` as const,
    `calc(100% - ${getSize(114)})` as const,
    `calc(100% - ${getSize(114)})` as const,
    PRINT_CONTENT_WIDTH
  );

  if (isNil(priceReport)) {
    return null;
  }

  return (
    <>
      <Helmet title={i18n.t('page.reasoning.title')} />
      <PageBackgroundGradient>
        <VStack shrink={1} maxWidth={315} width="100%" spacing={6}>
          <VehicleHeader
            priceReport={priceReport}
            catalogue={catalogue}
            data-testid={suffixTestId('vehicleHeader', props)}
          />
          <HStack
            spacing={6}
            justify="center"
            wrap={getResponsiveValue(true, false, false, false, false)}
          >
            <Hide onMobile when={isPrint}>
              <Box width={['100%', 80, 108]} flexShrink={0}>
                <StickyContainer
                  offset={4}
                  isDisabled={getValueByDevice(device, true, false)}
                  data-testid={suffixTestId('stickyContainer', props)}
                >
                  <VehicleParametersCard
                    priceReport={priceReport}
                    request={requestData}
                    catalogue={catalogue}
                    data-testid={suffixTestId('vehicleParametersCard', props)}
                  />
                </StickyContainer>
              </Box>
            </Hide>
            <VStack spacing={6} grow={1} maxWidth={maxContentWidth}>
              <Card
                variant={isPrint ? 'inlineWhite' : undefined}
                data-testid={props['data-testid']}
              >
                <Box padding={2}>
                  <VStack spacing={6} align="center">
                    <Heading align="center" size={2} data-testid={suffixTestId('title', props)}>
                      {i18n.t('page.reasoning.heading')}
                    </Heading>
                    <DataStatus isLoading={priceReport.status === 'PENDING'}>
                      <Segment
                        value={selectedSegment}
                        onChange={setSelectedSegment}
                        options={[
                          {
                            value: 'buying',
                            label: i18n.t('page.reasoning.buying'),
                          },
                          {
                            value: 'selling',
                            label: i18n.t('page.reasoning.selling'),
                          },
                        ]}
                      />
                      <VStack spacing={2}>
                        {(
                          priceReport?.arguments?.[
                            selectedSegment === 'buying' ? 'buyingArguments' : 'sellingArguments'
                          ] ?? []
                        )
                          .filter((argument): argument is string => isNotNil(argument))
                          .map((argument, index) => (
                            <Alert
                              key={index}
                              icon="action/ai"
                              data-testid={suffixTestId('argument', props)}
                            >
                              <Text size="xSmall" color="primary">
                                {argument.replace(/^• /, '')}
                              </Text>
                            </Alert>
                          ))}
                      </VStack>
                    </DataStatus>
                  </VStack>
                </Box>
              </Card>
            </VStack>
          </HStack>
        </VStack>
      </PageBackgroundGradient>
    </>
  );
}
